import { throttle, get } from 'lodash';
import { useRouter } from 'vue-router';
import { $emitter } from '@/services/emitter';
import i18n from '@/i18n';
import { AppErrorEnum } from './models/Enums';
import { showPlanLimitsErrorInToast } from '@/services/constants/errors-without-toast';
import { getBillingSubscriptionApiRequest } from '@/services/api/billing-api';
import { useStore } from '@/store';
const translate = (key: string, args?: unknown[]) => i18n.global.t(key, args);

export const useGlobalErrorsHandler = () => {
  const router = useRouter();
  const store = useStore();

  const initErrorsHandler = () => {
    const onRateLimitExceededThrottled = throttle((error: { response: { data: { payload: { seconds_to_wait_before_next_retry: number } } } }) => {
      const nextRetry = error.response.data.payload.seconds_to_wait_before_next_retry;
      $emitter.emit('toast-error', translate('Exceeded number of requests. Please retry in {0} sec', [nextRetry]));
    }, 3000);

    $emitter.on('request-error', async (error: {
      config?: { meta?: { disconnectCheck?: boolean; ignoreInterceptor?: boolean } };
      message?: string;
      response?: {
        status: number;
        data: {
          message?: string;
          payload?: { seconds_to_wait_before_next_retry?: number; twoFactorQrCode?: string };
          errors?: { keys: unknown; error: string }[];
          subscriptionRestriction?: boolean;
        };
      };
      request?: { responseURL?: string };
    }) => {
      const meta = error?.config?.meta || {};

      console.log(
        `%cError Handler (response) SHORT >>>> \nStatus: ${error.response?.status} \nData: ${JSON.stringify(error.response?.data)}`,
        'background: red; color: white',
      );
      console.debug('Error Handler (response) FULL >>>>', error.response);

      store.setLoading(false);

      if (error instanceof TypeError) {
        router.replace('/logout');
      }

      if (error.message === 'Network Error' && !meta.disconnectCheck) {
        $emitter.emit('toast-error', translate('The request failed. Please try again or contact the support team through live chat.'));
        return;
      }

      if (!error.response || meta.ignoreInterceptor) {
        return;
      }

      switch (error.response.status) {
        case 429:
          if (error.response.data.message === 'RATE_LIMIT_EXCEEDED') {
            onRateLimitExceededThrottled(error as { response: { data: { payload: { seconds_to_wait_before_next_retry: number } } } });
          }
          break;

        case 423:
          if (error.response.data.message === 'TWO_FACTOR_UNAUTHORIZED') {
            const twoFactorQrCode = get(error, 'response.data.payload.twoFactorQrCode');
            if (twoFactorQrCode) {
              store.member = { ...(store.member ?? {}), twoFactorQrCode };
            }

            store.isAuthorized = false;
            router.push('/two-factor');
          }
          break;

        case 401:
          if (
            error.request?.responseURL?.toLowerCase().indexOf('auth/login/member') === -1 &&
            error.request?.responseURL?.toLowerCase().indexOf('auth/login/support') === -1
          ) {
            if (error.response.data.message === 'UNAUTHORIZED') {
              router.replace('/logout');
              $emitter.emit('toast-error', translate(error.response.data.message));
            }
          }
          break;

        case 402:
          switch (error.response.data.message) {
            case AppErrorEnum.freePlanLimitsExceeded:
              store.appError = error.response.data.message;
              break;
            case AppErrorEnum.unpaidInvoices:
              store.appError = error.response.data.message;
              break;
            case AppErrorEnum.dataRetentionTooOldDataRetrieved:
              $emitter.emit('open-dialog', {
                name: 'ChangePlanDialog',
                data: {
                  planLimits: 'dataRetention',
                },
              });
              break;
            case AppErrorEnum.billingPaymentMethodVerificationFailed:
              store.appError = error.response.data.message;
              break;
            default:
              $emitter.emit('toast-error', translate(error.response.data.message || ''));
              break;
          }
          break;

        case 409:
          if (error.response.data.subscriptionRestriction) {
            store.setBilling(await getBillingSubscriptionApiRequest());
            if (showPlanLimitsErrorInToast(error)) {
              $emitter.emit('toast-error', translate(error.response.data.message || ''));
            }
          }
          break;
        case 403:
        case 400:
        case 500:
          if (Array.isArray(error.response.data.errors) && error.response.data.errors.length > 0) {
            const textError = error.response.data.errors
              .map((item) => translate(item.error, item.keys as unknown[]))
              .join('<br>');
            $emitter.emit('toast-error', textError);
          } else if (error.response.data.message) {
            $emitter.emit('toast-error', translate(error.response.data.message));
          }
          break;
        default:
          break;
      }
    });
  };

  return {
    initErrorsHandler,
  };
};
